import { ArrowLeftIcon } from '@heroicons/react/outline';
import walletIcon from 'assets/svg/icons/wallet-onboarding-icon.svg';
import { Button } from 'components/atoms/Button';
import CustomLoader from 'components/molecules/CustomLoader.component';
import useGetConnectors from 'hooks/Bank/useGetConnectors';
import { AnimatePresence, motion } from 'motion/react';
import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { Connector } from 'types/transactions.types';
import { getTracingEvents } from 'utils/amplitude';
import {
  WEBAPP_BANKING_NUDGES_CLICKEDTOPUPTAXRESERVE,
  WEBAPP_BANKING_PAYMENTTRANSFER_SUCCESSFULTRANSFER,
} from 'utils/amplitude/events/bank.amplitude';
import formatMoney from 'utils/formatMoney';
import WalletOnboardingModal from '../../BankingSignUp/components/WalletOnboarding.component';
import {
  getMainAccountableBank,
  getTaxSavingsAccountableBank,
  isUserConnectedToWallet,
} from '../helpers';
import InternalTransferComponent from '../MoneyTransfer/components/InternalTransfer.component';
import useTotalTaxesToSetAside from '../useTotalTaxesToSetAside';

const tracingEvents = getTracingEvents({
  onTransferSuccess: WEBAPP_BANKING_PAYMENTTRANSFER_SUCCESSFULTRANSFER,
});

const transition = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { delay: 0.25 } },
  exit: { opacity: 0 },
};

const WalletSetAsideToast: FC<{
  totalTaxesToSetAside: number;
  connectors?: Connector[];
  onTransferSuccess?: () => void;
}> = ({ totalTaxesToSetAside, connectors, onTransferSuccess }) => {
  const { t } = useTranslation();

  const [currentStep, setCurrentStep] = useState(0);

  const mainAccount = getMainAccountableBank(connectors as Connector[]);

  const taxReserveAccount = getTaxSavingsAccountableBank(
    connectors as Connector[],
  );

  const _amount = taxReserveAccount?.balance?.amount || 0;

  const isConnectedToWallet = isUserConnectedToWallet(connectors);

  const amountToSetAside = totalTaxesToSetAside - _amount;

  const amountExceedsMainAccountBalance =
    amountToSetAside > (mainAccount?.balance.amount || 0);

  const invoiceAmountWidth =
    _amount > totalTaxesToSetAside
      ? '100%'
      : `${(_amount / totalTaxesToSetAside) * 100}%`;

  const taxesAmountWidth =
    totalTaxesToSetAside > _amount
      ? '100%'
      : `${(totalTaxesToSetAside / _amount) * 100}%`;

  const steps = [
    <motion.div
      className="flex items-center flex-col relative h-full px-6 w-full"
      key="1"
    >
      <motion.div
        {...transition}
        className="w-10 h-10 p-1.5 bg-bank-100 rounded-full text-bank-900 stroke-bank-900 fill-bank-900"
      >
        <img src={walletIcon} />
      </motion.div>

      <motion.div {...transition} className="my-4 text-center">
        <h1 className="text-2xl font-bold text-primary-800 mx-auto">
          {t('accountable_banking.top_up.cta', {
            bankAccountName: t('wallet.tax_reserve_account'),
          })}
        </h1>

        <p className="text-primary-700 mt-2">
          {t('accountable_banking.nudges.set_aside_disclaimer', {
            amount: formatMoney(amountToSetAside),
          })}
        </p>
      </motion.div>

      <motion.div {...transition} className="w-full my-6">
        <div className="p-6 py-3 border border-taxes-100 border-b-0 rounded-t-lg rounded-b-0 flex items-center justify-between">
          <div className="w-[55%] saving-bar-grad-rotate rounded-lg">
            <motion.div
              initial={{ width: 0 }}
              animate={{
                width: invoiceAmountWidth,
                transition: { delay: 0.3 },
              }}
              style={{ width: invoiceAmountWidth }}
              className="h-9 rounded-lg bg-bank"
            />
          </div>

          <div className="flex-1 text-right">
            <h2 className="text-right -mb-1 text-bank font-semibold text-lg">
              {formatMoney(_amount)}
            </h2>

            <span className="text-right text-bank">
              {t('accountable_banking.nudges.already_set_aside.label')}
            </span>
          </div>
        </div>

        <div className="p-6 py-3 border border-taxes-100 rounded-b-lg rounded-t-0 flex items-center justify-between">
          <div className="w-[55%] taxes-bar-grad rounded-lg">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: taxesAmountWidth, transition: { delay: 0.3 } }}
              style={{ width: taxesAmountWidth }}
              className="h-9 rounded-lg bg-taxes flex overflow-hidden"
            />
          </div>

          <div className="flex-1 text-right">
            <h2 className="text-right -mb-1 text-taxes font-semibold text-lg">
              {formatMoney(totalTaxesToSetAside)}
            </h2>
            <span className="text-right text-taxes">
              {t('accountable_banking.nudges.future_taxes.label')}
            </span>
          </div>
        </div>
      </motion.div>

      {isConnectedToWallet ? (
        <motion.div className="w-full" {...transition}>
          <Button
            className="mt-6 w-full"
            color="bank"
            tracingEvent={WEBAPP_BANKING_NUDGES_CLICKEDTOPUPTAXRESERVE}
            onClick={() => setCurrentStep((step) => step + 1)}
          >
            {t('accountable_banking.top_up.cta', {
              bankAccountName: t('wallet.tax_reserve_account'),
            })}
          </Button>
        </motion.div>
      ) : (
        <WalletOnboardingModal
          name="Nudges Discover more button"
          Trigger={({ api }) => (
            <Button
              className="mt-6 w-full"
              color="bank"
              onClick={() => api.openModal()}
            >
              {t('accountable_banking.nudges.discover_more.cta')}
            </Button>
          )}
          // TODO: change this approach with dynamic one
          initialScreenIndex={2} // refers to FullTransparency step
        />
      )}
    </motion.div>,

    <motion.div
      className="flex items-center flex-col relative h-full px-6 w-full"
      key="2"
    >
      <motion.div
        {...transition}
        className="w-10 h-10 p-1.5 bg-bank-100 rounded-full text-bank-900 stroke-bank-900 fill-bank-900"
      >
        <img src={walletIcon} />
      </motion.div>

      <motion.div {...transition} className="my-4 text-center">
        <h1 className="text-2xl font-bold text-primary-800 w-[90%] mx-auto">
          {amountExceedsMainAccountBalance
            ? t('accountable_banking.nudges.insufficient_funds.title')
            : t('accountable_banking.nudges.title')}
        </h1>

        <p className="text-primary-700 mt-2">
          {amountExceedsMainAccountBalance
            ? t('accountable_banking.nudges.insufficient_funds.subtitle')
            : t('accountable_banking.nudges.subtitle')}
        </p>
      </motion.div>

      <InternalTransferComponent
        initialAmount={totalTaxesToSetAside}
        onTransferSuccess={onTransferSuccess}
        // hideHeading
        sourceAccount={mainAccount as Connector}
        destinationAccount={taxReserveAccount as Connector}
      />
    </motion.div>,
  ];

  return (
    <div className="relative">
      <AnimatePresence mode="wait">
        {currentStep > 0 ? (
          <motion.div className="absolute z-10 top-2 left-5" {...transition}>
            <Button
              asDefault
              className="flex !items-center space-x-2 mb-5 group"
              onClick={() => setCurrentStep((step) => step - 1)}
            >
              <ArrowLeftIcon className="h-4 text-primary-500 group-hover:text-primary-700" />

              <span className="text-primary-500 text-sm font-semibold group-hover:text-primary-700">
                {t('back')}
              </span>
            </Button>
          </motion.div>
        ) : null}

        {steps[currentStep]}
      </AnimatePresence>
    </div>
  );
};

export const WalletSetAsideToastWrapper: FC<{
  onTransferSuccess?: () => void;
}> = ({ onTransferSuccess }) => {
  const { totalTaxesToSetAside, isLoading: isLoadingTotalToSetAside } =
    useTotalTaxesToSetAside();

  const connectorsQuery = useGetConnectors();

  if (isLoadingTotalToSetAside || connectorsQuery.isLoading)
    return (
      <div className="h-[400px] flex items-center justify-center">
        <CustomLoader color="bank" />
      </div>
    );

  return (
    <WalletSetAsideToast
      connectors={connectorsQuery.data as Connector[]}
      totalTaxesToSetAside={totalTaxesToSetAside}
      onTransferSuccess={onTransferSuccess}
    />
  );
};
